import { AlertTriangle, Check, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Booking } from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';
import { BookingTabStatus } from '../hooks/useBookingTabStatus';

interface BookingTabHeaderProps {
  booking: Booking;
  overallStatus: BookingTabStatus['overallStatus'];
  vendorCounts: BookingTabStatus['vendorCounts'];
}

/**
 * Header component for the booking tab showing booking info and overall status.
 */
export const BookingTabHeader: React.FC<BookingTabHeaderProps> = ({
  booking,
  overallStatus,
  vendorCounts
}) => {
  const { t } = useTranslation('visitor-management');

  const getStatusBadge = () => {
    switch (overallStatus) {
      case 'all-synced':
        return (
          <Badge variant="success" tone="subtle" size="sm" className="gap-1">
            <Check className="w-3 h-3" />
            <span>All Synced</span>
          </Badge>
        );
      case 'has-drift':
        return (
          <Badge variant="attention" tone="subtle" size="sm" className="gap-1">
            <AlertTriangle className="w-3 h-3" />
            <span>Drift Detected</span>
          </Badge>
        );
      case 'has-errors':
        return (
          <Badge variant="danger" tone="subtle" size="sm" className="gap-1">
            <X className="w-3 h-3" />
            <span>Errors</span>
          </Badge>
        );
      case 'not-eligible':
        return (
          <Badge variant="gray" tone="subtle" size="sm" className="gap-1">
            <span>Not Eligible</span>
          </Badge>
        );
      case 'no-vendors':
        return (
          <Badge variant="gray" tone="subtle" size="sm">
            <span>No Vendors</span>
          </Badge>
        );
    }
  };

  const getStatusSummary = () => {
    if (vendorCounts.total === 0) {
      return 'No vendors configured';
    }

    const { synced, drifted, errors } = vendorCounts;
    const parts: string[] = [];

    if (synced > 0) parts.push(`${synced} synced`);
    if (drifted > 0) parts.push(`${drifted} drifted`);
    if (errors > 0) parts.push(`${errors} errors`);

    return parts.join(', ');
  };

  return (
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Visitor Management
            </h2>
            {getStatusBadge()}
          </div>

          <div className="text-sm text-gray-600 space-y-1">
            <div>
              <span className="font-medium">Booking:</span>{' '}
              <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                #{booking.id}
              </span>
            </div>

            {booking.contact && (
              <div>
                <span className="font-medium">Contact:</span>{' '}
                {booking.contact.name ||
                  `${booking.contact.first_name || ''} ${booking.contact.last_name || ''}`.trim() ||
                  'Unnamed Contact'}
              </div>
            )}

            {booking.events && booking.events.length > 0 && (
              <div>
                <span className="font-medium">Dates:</span>{' '}
                {new Date(booking.events[0].start_date).toLocaleDateString()} -{' '}
                {new Date(booking.events[0].end_date).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div className="text-xs text-gray-500">
          <span className="font-medium">Vendor Status:</span>{' '}
          {getStatusSummary()}
        </div>
      </div>
    </div>
  );
};
